import {
  newChoiceId,
  newFieldId,
  type FieldType,
  type FormField,
} from "@koaform/form-schema";

/**
 * The block palette. Glyphs are from the design system's QUESTION BLOCKS
 * panel — a monospace character rather than an icon set, which keeps the
 * palette weightless and consistent with the mono labels used elsewhere.
 */
/**
 * The block palette, grouped.
 *
 * Twelve blocks in one flat list meant scanning the whole thing to find
 * "Email". Grouping by what you are asking for — words, a choice, contact
 * details, a file, layout — makes it a glance instead of a read.
 *
 * Glyphs are monospace characters from the design's QUESTION BLOCKS panel
 * rather than an icon set, which keeps the palette weightless.
 */
export type PaletteItem = {
  type: FieldType;
  label: string;
  glyph: string;
  hint: string;
};

export const FIELD_GROUPS: { group: string; items: PaletteItem[] }[] = [
  {
    group: "Text",
    items: [
      { type: "short_text", label: "Short answer", glyph: "Ab", hint: "A single line of text" }, // prettier-ignore
      { type: "long_text", label: "Long answer", glyph: "\u00b6", hint: "A paragraph" }, // prettier-ignore
    ],
  },
  {
    group: "Choice",
    items: [
      { type: "multiple_choice", label: "Multiple choice", glyph: "\u25c9", hint: "Pick one" }, // prettier-ignore
      { type: "checkboxes", label: "Checkboxes", glyph: "\u2611", hint: "Pick several" }, // prettier-ignore
      { type: "dropdown", label: "Dropdown", glyph: "\u25be", hint: "Pick one from a list" }, // prettier-ignore
      { type: "rating", label: "Rating", glyph: "\u2605", hint: "A star rating" }, // prettier-ignore
    ],
  },
  {
    group: "Details",
    items: [
      { type: "email", label: "Email", glyph: "@", hint: "A validated email address" }, // prettier-ignore
      { type: "number", label: "Number", glyph: "#", hint: "A numeric answer" }, // prettier-ignore
      { type: "date", label: "Date", glyph: "\u25a6", hint: "A calendar date" }, // prettier-ignore
      { type: "file_upload", label: "File upload", glyph: "\u2191", hint: "Attach a file" }, // prettier-ignore
    ],
  },
  {
    group: "Layout",
    items: [
      { type: "heading", label: "Heading", glyph: "H", hint: "A section title" }, // prettier-ignore
      { type: "paragraph", label: "Text", glyph: "T", hint: "Help text, not a question" }, // prettier-ignore
    ],
  },
];

/** Flat view, for lookups that do not care about grouping. */
export const FIELD_MENU: PaletteItem[] = FIELD_GROUPS.flatMap((g) => g.items);

export const fieldGlyph = (type: FieldType): string =>
  FIELD_MENU.find((m) => m.type === type)?.glyph ?? "?";

const twoChoices = () => [
  { id: newChoiceId(), label: "Option 1" },
  { id: newChoiceId(), label: "Option 2" },
];

/**
 * Every new field gets a fresh id from `newFieldId()`. Ids are never reused —
 * that is what keeps historical responses readable when a form changes.
 */
export const makeField = (type: FieldType): FormField => {
  const id = newFieldId();

  switch (type) {
    case "heading":
      return { id, type, text: "Section heading", level: 2 };
    case "paragraph":
      return { id, type, text: "Some help text." };
    case "short_text":
      return { id, type, label: "Question", required: false, maxLength: 500 };
    case "long_text":
      return {
        id,
        type,
        label: "Question",
        required: false,
        maxLength: 5000,
        rows: 4,
      };
    case "multiple_choice":
      return {
        id,
        type,
        label: "Question",
        required: false,
        choices: twoChoices(),
        allowOther: false,
        randomize: false,
      };
    case "checkboxes":
      return {
        id,
        type,
        label: "Question",
        required: false,
        choices: twoChoices(),
        allowOther: false,
        randomize: false,
      };
    case "dropdown":
      return { id, type, label: "Question", required: false, choices: twoChoices() }; // prettier-ignore
    case "email":
      return { id, type, label: "Email", required: false };
    case "number":
      return { id, type, label: "Question", required: false, decimals: 0 };
    case "date":
      return { id, type, label: "Question", required: false, mode: "date" };
    case "rating":
      return { id, type, label: "Question", required: false, shape: "star", max: 5 }; // prettier-ignore
    case "file_upload":
      return {
        id,
        type,
        label: "Upload a file",
        required: false,
        maxFiles: 1,
        maxSizeMb: 10,
        accept: [],
      };
  }
};

export const fieldTypeLabel = (type: FieldType): string =>
  FIELD_MENU.find((m) => m.type === type)?.label ?? type;

export const isLayout = (type: FieldType) =>
  type === "heading" || type === "paragraph";

/**
 * Re-types a field, carrying over everything the new type understands.
 *
 * The id is kept here on purpose: whether this particular change may keep its
 * id is the caller's call — Builder.changeField already mints a new one when
 * `isLosslessTypeChange` says stored answers could not survive the switch.
 */
export const convertFieldType = (
  field: FormField,
  to: FieldType,
): FormField => {
  if (field.type === to) return field;

  const from = field as Record<string, unknown>;
  const next = { ...makeField(to), id: field.id } as Record<string, unknown>;

  // The question survives a type change; so does a heading's text either way.
  const title = (from.label ?? from.text) as string | undefined;
  if (title) next[isLayout(to) ? "text" : "label"] = title;

  if (!isLayout(to)) {
    if (typeof from.required === "boolean") next.required = from.required;
    if (from.description) next.description = from.description;
    if (from.placeholder && "placeholder" in next) next.placeholder = from.placeholder; // prettier-ignore
  }
  // Choice ids travel between choice types — mc → dropdown keeps its options.
  if ("choices" in next && Array.isArray(from.choices)) next.choices = from.choices; // prettier-ignore
  if ("allowOther" in next && typeof from.allowOther === "boolean") next.allowOther = from.allowOther; // prettier-ignore
  if ("randomize" in next && typeof from.randomize === "boolean") next.randomize = from.randomize; // prettier-ignore

  return next as FormField;
};
